<template>
  <Card class="rate-quote-detail-card">
    <template #content>
      <div class="rate-quote-detail-section carrier-section">
        <RateQuoteCarrier :rate-quote="rateQuote" />

        <RateQuoteWarnings :rate-quote="rateQuote" mode="CHIP" />
      </div>

      <div class="rate-quote-detail-section">
        <KeyValuePair :label="t('carriers.modes')" :value="modeDisplayValue" />
      </div>

      <div class="rate-quote-detail-section">
        <KeyValuePair
          :label="t('rateShop.service')"
          :value="serviceDisplayValue"
        />
      </div>

      <div class="rate-quote-detail-section">
        <KeyValuePair
          :label="t('rateShop.transit')"
          :value="transitDisplayValue"
        />
      </div>

      <div class="rate-quote-detail-section">
        <KeyValuePair
          :label="t('rateShop.rateSource')"
          :value="rateSourceText"
        />
      </div>

      <div class="rate-quote-detail-section">
        <KeyValuePair
          :label="t('common.totalParam', { param: t('rateShop.price') })"
          :value="priceDisplayValue"
        >
          <template #value="{ value }">
            <span class="text-success font-medium">
              {{ value }}
            </span>
          </template>
        </KeyValuePair>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney, titleCase, translateEnum } from '@rygen/rygen-ui/i18n';

import { RateQuote } from '@/model';

import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RateQuoteWarnings from '@/components/rateShop/RateQuoteWarnings.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Card from 'primevue/card';

const props = defineProps({
  rateQuote: {
    type: Object as PropType<RateQuote>,
    default: () => new RateQuote(),
  },
});
const { rateQuote } = toRefs(props);

const { t } = useI18n();

const renderServiceInfo = (rateQuote: RateQuote) => {
  return [rateQuote.serviceTypeDescription, rateQuote.service]
    .filter((x) => x)
    .map((x) => titleCase(x))
    .join(' • ');
};

const rateSourceText = computed(() => {
  return rateQuote.value.isCapacityProvider
    ? t('rateShop.capacityProvider')
    : t(`rateShop.source.${rateQuote.value.source}`);
});

const modeDisplayValue = computed(() => {
  const mode = rateQuote.value.mode
    ? translateEnum(rateQuote.value.mode, 'Mode')
    : '-';
  const equipment = rateQuote.value.equipmentType?.name;

  return equipment ? `${mode} • ${equipment}` : mode;
});

const serviceDisplayValue = computed(() => {
  const serviceType = rateQuote.value.serviceType
    ? titleCase(rateQuote.value.serviceType)
    : '-';
  const serviceInfo = renderServiceInfo(rateQuote.value);

  return serviceInfo ? `${serviceType} • ${serviceInfo}` : serviceType;
});

const transitDisplayValue = computed(() => {
  return rateQuote.value.transitTime
    ? t('rateShop.countDays', { count: rateQuote.value.transitTime })
    : '-';
});

const priceDisplayValue = computed(() => {
  return renderMoney(rateQuote.value.totalPrice, {
    currencyCode: rateQuote.value.currencyCode,
  });
});
</script>

<style>
.rate-quote-detail-card.p-card {
  .p-card-content {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: var(--gap-1);

    .rate-quote-detail-section {
      flex: 1;
      min-width: 0;

      &.carrier-section {
        flex: 2;
        display: flex;
        flex-direction: column;
        gap: var(--gap-1);
      }
    }
  }
}
</style>
